import React, { useState, useEffect } from 'react';
import {
    X,
    Divide,
    Equal,
    ArrowDownWideNarrow,
    ArrowDownCircle,
    Minus,
    Info
} from 'lucide-react';
import {
    Link,
    Skeleton,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import ExportCSVComponent from 'src/components/ExportCSVComponent';
import { TableSortControls } from 'src/components/TableSortControls';
import { applySorting, getComparator } from 'src/utils/filterAndSortData';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import dayjs from 'dayjs';

const useStyles = makeStyles()((theme) => ({
    section: {
        // section styles if needed
    },
    headerContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
        marginBottom: '10px',
        position: 'relative',
        gap: 5
    },
    heading: {
        marginRight: 'var(--space-s)'
    },
    iconGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2,2fr)',
        gridTemplateRows: 'repeat(2,2fr)'
    },
    box: {
        // box styles from m-box
    },
    mobileControls: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between'
    },
    fppsRateContainer: {
        display: 'flex',
        flexWrap: 'wrap'
    },
    fppsRateText: {
        margin: 'var(--space-s) 0'
    },
    clickToCollapse: {
        display: 'flex',
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'right',
        alignItems: 'center',
        padding: '1rem',
        width: 'fit-content',
        transition: 'opacity .5s ease-in-out'
    },
    collapseButton: {
        display: 'flex',
        alignItems: 'center',
        gap: 5
    },
    upArrow: {
        margin: '0 var(--space-s) 0 0',
        transition: 'transform .2s ease-in-out'
    },
    tableContainer: {
        position: 'relative'
    },
    clickToExpand: {
        flex: 1,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'absolute',
        zIndex: 2,
        transform: 'translate(-50%,-50%)',
        left: '50%',
        bottom: 0,
        transition: 'opacity 1s ease-in-out',
        gap: 5
    },
    downArrow: {
        margin: '0 0 0 var(--space-s)',
        transition: 'transform .2s ease-in-out'
    },
    animateCollapsible: {
        transition: 'max-height .5s cubic-bezier(0.87, 0, 0.13, 1)',
        borderTop: '1px solid var(--color-border)',
        borderBottom: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-l)',
        width: '100%',
        display: 'grid',
        position: 'relative'
    },
    collapsible: {
        maxHeight: '10rem',
        overflow: 'hidden'
    },
    openCollapsible: {
        maxHeight: '75vh',
        overflowX: 'auto',
        '&::-webkit-scrollbar': {
            width: '8px'
        },
        '&::-webkit-scrollbar-track': {
            background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
            background: 'var(--neutral-8)',
            borderRadius: '4px'
        },
        '&::-webkit-scrollbar-corner': {
            background: 'transparent'
        },
        '&::-webkit-scrollbar-thumb:hover': {
            background: 'var(--neutral-5)'
        }
    },
    table: {
        overflow: 'visible',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-l)'
    },
    tableHeader: {
        position: 'sticky',
        top: '-1px'
    },
    sortLabel: {
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        alignItems: 'center'
    },
    sortArrow: {
        transition: 'transform .2s ease-in-out, opacity .2s ease-in-out',
        width: '15px',
        height: '15px',
        margin: '0 0 0 var(--space-s)'
    },
    gradient: {
        position: 'absolute',
        width: '100%',
        height: '10rem',
        right: 0,
        bottom: 0,
        left: 0,
        transition: 'opacity .6s ease-in-out, max-height .6s ease-in-out',
        background: 'linear-gradient(0deg, rgba(23,25,24,1) 0%, rgba(23,25,24,0) 100%)'
    },
    gradientOn: {
        opacity: 1,
        cursor: 'pointer',
        maxHeight: '10rem'
    },
    gradientOff: {
        opacity: 0,
        maxHeight: 0
    },
    mobileCard: {
        margin: 'var(--space-m) 0'
    },
    cardRows: {
        display: 'flex',
        padding: 'var(--space-s) 0',
        boxShadow: '0px 1px 0px var(--color-border)',
        '&:last-of-type': {
            boxShadow: 'none'
        }
    },
    cardRowContent: {
        justifyContent: 'space-between'
    },
    calcBrkdnTtlsContainerMobile: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        margin: 'var(--space-xl) 0 0 0'
    },
    mobileTotalRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    calcTtlsLabel: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        margin: '10px 0',
        maxWidth: '10ch'
    },
    calcTtlsValue: {
        margin: '10px 0',
        textAlign: 'left',
        minWidth: '9ch'
    },
    flexRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center'
    },
    mobileInfo: {
        '&:hover': {
            textDecoration: 'none'
        }
    },
    mobileInfoLabel: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        margin: '2px 2px 10px 0',
        textDecoration: 'underline',
        textDecorationStyle: 'dotted',
        textUnderlineOffset: '4px'
    },
    resultsBox: {
        padding: 'var(--space-default) var(--space-xxl)'
    },
    resultsEquation: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        marginLeft: 'var(--space-xl)'
    },
    totals: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        marginLeft: 'var(--space-xl)',
        justifyContent: 'space-between',
        '& > p:first-of-type': {
            margin: 'var(--space-l) 0',
            textAlign: 'left'
        },
        '& > p:nth-of-type(2), & > p:nth-of-type(3)': {
            textAlign: 'left',
            minWidth: 'calc(10ch + var(--space-xl) + var(--space-xl))',
            paddingLeft: 'var(--space-xl)'
        }
    },
    fppsRateRow: {
        position: 'relative'
    },
    fppsRateTextWithMargin: {
        margin: 'var(--space-l) 4px var(--space-l) 0'
    },
    infoIconContainer: {
        position: 'absolute',
        top: 28,
        left: '11ch'
    },
    skeletonMobile: {
        maxWidth: '300px'
    },
    skeletonDesktop: {
        maxWidth: '300px'
    },
    scrollbar: {
        '&::-webkit-scrollbar': {
            width: '8px'
        },
        '&::-webkit-scrollbar-track': {
            background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
            background: 'var(--neutral-8)',
            borderRadius: '4px'
        },
        '&::-webkit-scrollbar-corner': {
            background: 'transparent'
        },
        '&::-webkit-scrollbar-thumb:hover': {
            background: 'var(--neutral-5)'
        }
    }
}));

const PPLNSCalculationBreakdown = ({ date, onDataLoaded }) => {
    const { classes, cx } = useStyles();
    const theme = useTheme();
    const mediumDevice = useMediaQuery(theme.breakpoints.down('md'));
    const { enqueueSnackbar } = useSnackbar();

    // Local state
    const [collapse, setCollapse] = useState(false);
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('height');
    const [exportInProcess, setExportInProcess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [blocksOfDay, setBlocksOfDay] = useState([]);
    const [totalFees, setTotalFees] = useState(0);
    const [totalFeesMinusExcluded, setTotalFeesMinusExcluded] = useState(0);
    const [blockSubsidy, setBlockSubsidy] = useState(3.125);

    const blockSubsidyValues = [50, 25, 12.5, 6.25, 3.125, 1.5625, 0.78125, 0.390625];

    // Helper function to calculate block subsidy by block height
    const blockSubsidyByBlockHeight = (blockHeight) => {
        if (typeof blockHeight !== 'number') blockHeight = parseInt(blockHeight);
        switch (true) {
            case (blockHeight >= 1470000 && blockHeight < 1680000):
                return 0.390625;
            case (blockHeight >= 1260000 && blockHeight < 1470000):
                return 0.78125;
            case (blockHeight >= 1050000 && blockHeight < 1260000):
                return 1.5625;
            case (blockHeight >= 840000 && blockHeight < 1050000):
                return 3.125;
            case (blockHeight >= 630000 && blockHeight < 840000):
                return 6.25;
            case (blockHeight >= 420000 && blockHeight < 630000):
                return 12.5;
            case (blockHeight >= 210000 && blockHeight < 420000):
                return 25;
            default:
                return 50;
        }
    };

    // Sorting functions
    const handleColumnSort = tableColumn => {
        const isAsc = order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(tableColumn);
        setBlocksOfDay(data => applySorting(data, getComparator(isAsc ? 'asc' : 'desc', tableColumn)));
    };

    const handleMobileCardsSort = (tableColumn, columnOrder) => {
        setOrder(columnOrder);
        setOrderBy(tableColumn);
        setBlocksOfDay(data => applySorting(data, getComparator(columnOrder, tableColumn)));
    };

    // PPLNS calculation function
    const handlePPLNSCalc = () => {
        return (
            dayjs(date).isBefore(dayjs('2023/11/30'))
                ? ((totalFees / 100000000) / (blocksOfDay.length * blockSubsidy) + 1) * 100
                : ((totalFeesMinusExcluded / 100000000) / ((blocksOfDay.length - 6) * blockSubsidy) + 1) * 100
        );
    };

    // CSV export function
    const getCSVData = async () => {
        setExportInProcess(true);

        if (blocksOfDay) {
            if (blocksOfDay.length === 0) {
                enqueueSnackbar('No data to export', {
                    variant: 'warning',
                });
                setExportInProcess(false);
                return [];
            }
            else {
                setExportInProcess(false);
                return blocksOfDay.map(i => {
                    return {
                        'Mining Pool': i.pool_slug === "foundryusa" ? "Foundry Pool USA" : 'Other',
                        'Blockheight': i.height,
                        'Status': i.included ? "Included" : "Excluded",
                        'Transaction Fees Per Block': i.total_fee_amt / 100000000,
                        'Block Subsidy': blockSubsidyByBlockHeight(i.height),
                    };
                });
            }
        }
        else {
            enqueueSnackbar('Something went wrong', {
                variant: 'error',
            });

            setExportInProcess(false);
            return [];
        }
    };

    // API call functions
    const getStartingBlockByTimeSelected = async () => {
        let blockHeight;
        try {
            const response = await axios.get(`https://mempool.reddfive.com/api/v1/mining/blocks/timestamp/${dayjs(date).unix()}`);
            if (response.status === 200) {
                if (!(dayjs(date).isSame(response.data, 'day'))) {
                    return blockHeight = response.data.height + 1;
                }
                else {
                    return blockHeight = response.data.height;
                }
            } else {
                throw new Error('PPLNS is currently offline');
            }
        } catch (error) {
            console.error('Error:', error.message);
            enqueueSnackbar('PPLNS is currently offline', {
                variant: 'error'
            });
        }
    };

    const getEndingBlockByTimeSelected = async () => {
        let blockHeight;
        try {
            const response = await axios.get(`https://mempool.reddfive.com/api/v1/mining/blocks/timestamp/${dayjs(date).endOf("day").unix()}`);
            if (response.status === 200) {
                if (!(dayjs(date).isSame(response.data, 'day'))) {
                    return blockHeight = response.data.height;
                }
                else {
                    return blockHeight = response.data.height - 1;
                }
            } else {
                throw new Error('PPLNS is currently offline');
            }
        } catch (error) {
            console.error('Error:', error.message);
            enqueueSnackbar('PPLNS is currently offline', {
                variant: 'error'
            });
        }
    };

    const getBlocksOfDay = async (height) => {
        let minHeight = height[0];
        let maxHeight = height[1];
        try {
            const response = await axios.get(`https://mempool.reddfive.com/api/v1/blocks-bulk/${minHeight}/${maxHeight}`);
            if (response.status === 200) {
                return response.data;
            } else {
                throw new Error('PPLNS is currently offline');
            }
        } catch (error) {
            console.error('Error:', error.message);
            enqueueSnackbar('PPLNS is currently offline', {
                variant: 'error'
            });
        }
    };

    // Fetch data when date changes
    useEffect(() => {
        setLoading(true);

        const blockHeightPromise = Promise.all([getStartingBlockByTimeSelected(), getEndingBlockByTimeSelected()]);
        blockHeightPromise.then((value) => {
            return getBlocksOfDay(value);
        }).then(results => {
            let top3 = results
                .slice()
                .sort((a, b) => b.total_fee_amt - a.total_fee_amt)
                .slice(0, 3);

            let bot3 = results
                .slice()
                .sort((a, b) => b.total_fee_amt - a.total_fee_amt)
                .slice(-3);

            let resultsArr = results.map(item => {
                return (
                    {
                        height: item.height,
                        pool_id: item.pool_id,
                        pool_slug: item.pool_slug,
                        total_fee_amt: item.total_fee_amt,
                        included: true
                    }
                );
            });

            top3.map(x => {
                let item = resultsArr.find(item => {
                    return item.height === x.height;
                });
                if (item) {
                    item.included = false;
                }
                return item;
            });

            bot3.map(x => {
                let item = resultsArr.find(item => {
                    return item.height === x.height;
                });
                if (item) {
                    item.included = false;
                }
                return item;
            });
            const feeResult = resultsArr.reduce((sum, { total_fee_amt }) => sum + total_fee_amt, 0);
            const excludedResult = resultsArr.filter(item => item.included).reduce((sum, { total_fee_amt }) => sum + total_fee_amt, 0);
            setTotalFees(feeResult);
            setTotalFeesMinusExcluded(excludedResult);
            const sortedBlocksOfDay = applySorting(resultsArr, getComparator('desc', 'height'));
            setBlocksOfDay(sortedBlocksOfDay);
            let blockSubsidySum = sortedBlocksOfDay.reduce((accumulator, currentValue) => accumulator + blockSubsidyByBlockHeight(currentValue.height), 0);
            const avgBlockSubsidy = blockSubsidySum / sortedBlocksOfDay.length;
            setBlockSubsidy(avgBlockSubsidy);
            setLoading(false);

            // Notify parent component of loaded data
            if (!!onDataLoaded) {
                onDataLoaded({
                    blockSubsidy: avgBlockSubsidy,
                    totalFees: feeResult,
                    totalFeesMinusExcluded: excludedResult,
                    blocksOfDayLength: sortedBlocksOfDay.length,
                    loading: false,
                    halvingDate: !blockSubsidyValues.includes(avgBlockSubsidy)
                });
            }
        });
    }, [date]);

    return (
        <section className='m-section'>
            <div className={classes.headerContainer}>
                <p className={cx("m-heading is-size-xl is-accent", classes.heading)}>Calculation Breakdown</p>
                <div className={classes.iconGrid}>
                    <X size={12} className='m-text is-accent' />
                    <Divide size={12} className='m-text is-accent' />
                    <Minus size={12} className='m-text is-accent' />
                    <Equal size={12} className='m-text is-accent' />
                </div>
            </div>

            <div>
                <div className='m-box'>
                    {!mediumDevice &&
                        <ExportCSVComponent
                            fileName={"PPLNS Rate"}
                            exportInProcess={exportInProcess}
                            getCSVData={getCSVData}
                            singleDate={true}
                            reportDate={dayjs(date).format('MMMM DD, YYYY')}
                            reportName="PPLNS Rate"
                            groupReport={true}
                            pdfOption={false}
                            variant={"contained"}
                        />
                    }
                    {mediumDevice &&
                        <div className={classes.mobileControls}>
                            <TableSortControls
                                selectorValues={{
                                    clear: 'Clear',
                                    height: 'Blockheight',
                                    included: 'Status',
                                    total_fee_amt: 'Transaction Fees Per Block'
                                }}
                                onChange={handleMobileCardsSort}
                                defaultSort='height'
                                defaultOrder='desc'
                            />
                            <ExportCSVComponent
                                fileName={"PPLNS Rate"}
                                exportInProcess={exportInProcess}
                                getCSVData={getCSVData}
                                singleDate={true}
                                reportDate={dayjs(date).format('MMMM DD, YYYY')}
                                reportName="PPLNS Rate"
                                groupReport={true}
                                pdfOption={false}
                                variant={"contained"}
                            />
                        </div>
                    }
                    <div className={classes.fppsRateContainer}>
                        <p className={cx('m-text is-size-l', classes.fppsRateText)}>PPLNS Rate: </p>
                        <div className={classes.clickToCollapse} style={{ opacity: collapse ? 1 : 0 }} >
                            <div
                                onClick={() => setCollapse(false)}
                                className={classes.collapseButton}
                                style={{ cursor: collapse ? 'pointer' : 'default' }}
                            >
                                <ArrowDownCircle
                                    className={cx('m-text', classes.upArrow)}
                                    size={16}
                                    style={{
                                        color: 'var(--color-text-link-on-danger)',
                                        transform: collapse ? 'rotate(180deg)' : 'rotate(360deg)'
                                    }}
                                />
                                <p className='m-text' style={{ color: 'var(--color-text-link-on-danger)' }}>Click to collapse</p>
                            </div>
                        </div>
                    </div>
                    {!mediumDevice &&
                        <div className={classes.tableContainer}>
                            <div
                                className={classes.clickToExpand}
                                style={{
                                    opacity: collapse ? 0 : 1,
                                    cursor: collapse ? 'default' : 'pointer'
                                }}
                                onClick={() => setCollapse(true)}
                            >
                                <p className='m-text'>Click to expand</p>
                                <ArrowDownCircle className={cx('m-text', classes.downArrow)} size={16} />
                            </div>
                            <div className={cx(classes.animateCollapsible, collapse ? classes.openCollapsible : classes.collapsible)}>
                                <table className={cx('m-table m-text', classes.table)}>
                                    <thead className={cx("m-table-header", classes.tableHeader)}>
                                        <tr className="m-table-row">
                                            <th className="m-table-cell m-table-column-header-cell">
                                                <p className='m-text'>Mining Pool</p>
                                            </th>
                                            <th
                                                className="m-table-cell m-table-column-header-cell"
                                                onClick={() => handleColumnSort('height')}
                                                title='Sort by blockheight'
                                            >
                                                <div className={classes.sortLabel}>
                                                    <p>Blockheight</p>
                                                    <ArrowDownWideNarrow
                                                        className={classes.sortArrow}
                                                        style={{
                                                            transform: order === 'asc' ? 'rotate(0deg)' : 'rotate(180deg)',
                                                            opacity: orderBy === 'height' ? 1 : 0
                                                        }}
                                                    />
                                                </div>
                                            </th>

                                            <th
                                                className="m-table-cell m-table-column-header-cell"
                                                onClick={() => handleColumnSort('included')}
                                                title='Sort by status'
                                            >
                                                <div className={classes.sortLabel}>
                                                    <p className='m-text'>Status</p>
                                                    <ArrowDownWideNarrow
                                                        className={classes.sortArrow}
                                                        style={{
                                                            transform: order === 'asc' ? 'rotate(0deg)' : 'rotate(180deg)',
                                                            opacity: orderBy === 'included' ? 1 : 0
                                                        }}
                                                    />
                                                </div>
                                            </th>

                                            <th
                                                className="m-table-cell m-table-column-header-cell"
                                                onClick={() => handleColumnSort('total_fee_amt')}
                                                title='Sort by transaction fees per block'
                                                style={{ width: '30ch' }}
                                            >
                                                <div className={classes.sortLabel}>
                                                    <p className='m-text'>Transaction Fees Per Block (BTC)</p>
                                                    <ArrowDownWideNarrow
                                                        className={classes.sortArrow}
                                                        style={{
                                                            transform: order === 'asc' ? 'rotate(0deg)' : 'rotate(180deg)',
                                                            opacity: orderBy === 'total_fee_amt' ? 1 : 0
                                                        }}
                                                    />
                                                </div>
                                            </th>

                                            <th className="m-table-cell m-table-column-header-cell">
                                                <p className='m-text'>Block Subsidy (BTC)</p>
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody className="m-table-body">
                                        {
                                            blocksOfDay.map(row => {
                                                return (
                                                    <tr
                                                        key={row.height}
                                                        className="m-table-row"
                                                        style={{
                                                            backgroundColor: row.pool_slug === "foundryusa" ? "#172921 !important" : "",
                                                            color: !row.included && !dayjs(date).isBefore(dayjs('2023/11/30')) ? "#adb5b277" : ''
                                                        }}
                                                    >
                                                        <td className="m-table-cell">
                                                            {loading ?
                                                                <Skeleton variant="rounded" width={"100%"} height={43} sx={{ bgcolor: 'var(--neutral-7)' }} />
                                                                :
                                                                row.pool_slug === "foundryusa" ?
                                                                    <div className="m-avatar is-size-xxl is-rounded" title="Foundry USA Pool">
                                                                        <img
                                                                            className="m-avatar-img"
                                                                            style={{
                                                                                filter: !row.included && !dayjs(date).isBefore(dayjs('2023/11/30')) ? "brightness(0.3)" : ''
                                                                            }}
                                                                            src={row.pool_slug === "foundryusa" ? '/android-chrome-192x192.png' : ""}
                                                                            alt="pool icon"
                                                                        />
                                                                    </div>
                                                                    : <p
                                                                        className="m-text is-size-s"
                                                                        style={{
                                                                            color: !row.included && !dayjs(date).isBefore(dayjs('2023/11/30')) ? "#adb5b277" : ''
                                                                        }}
                                                                    >
                                                                        Other
                                                                    </p>
                                                            }
                                                        </td>
                                                        <td className="m-table-cell">
                                                            {loading ?
                                                                <Skeleton variant="rounded" width={"100%"} height={43} sx={{ bgcolor: 'var(--neutral-7)' }} />
                                                                :
                                                                row.height
                                                            }
                                                        </td>

                                                        <td className="m-table-cell">
                                                            {loading ?
                                                                <Skeleton variant="rounded" width={"100%"} height={43} sx={{ bgcolor: 'var(--neutral-7)' }} />
                                                                :
                                                                dayjs(date).isBefore(dayjs('2023/11/30')) ? "Included" : row.included ? "Included" : "Excluded"
                                                            }
                                                        </td>

                                                        <td className="m-table-cell">
                                                            {loading ?
                                                                <Skeleton variant="rounded" width={"100%"} height={43} sx={{ bgcolor: 'var(--neutral-7)' }} />
                                                                :
                                                                row.total_fee_amt / 100000000
                                                            }
                                                        </td>

                                                        <td className="m-table-cell">
                                                            {loading ?
                                                                <Skeleton variant="rounded" width={"100%"} height={43} sx={{ bgcolor: 'var(--neutral-7)' }} />
                                                                :
                                                                blockSubsidyByBlockHeight(row.height)
                                                            }
                                                        </td>
                                                    </tr>
                                                )
                                            })
                                        }
                                    </tbody>
                                </table>
                            </div>
                            <div
                                className={cx(classes.gradient, collapse ? classes.gradientOff : classes.gradientOn)}
                                onClick={() => setCollapse(true)}
                            ></div>
                        </div>
                    }
                    {mediumDevice &&
                        <div className={classes.tableContainer}>
                            <div
                                style={{ border: "none" }}
                                className={cx(classes.animateCollapsible, collapse ? classes.openCollapsible : classes.collapsible)}
                            >
                                {applySorting(blocksOfDay, getComparator(order, orderBy)).map((row) => {
                                    return (
                                        <div
                                            key={row.height}
                                            className={cx("m-box", classes.mobileCard)}
                                            style={row.included ? {} : { border: "1px solid var(--color-text-link-on-danger)" }}
                                        >
                                            <div className={cx(classes.cardRows, classes.cardRowContent)}>
                                                <p className="m-text">Mining Pool</p>
                                                {loading ?
                                                    <Skeleton variant="rounded" width={"100%"} height={43} sx={{ bgcolor: 'var(--neutral-7)' }} />
                                                    :
                                                    row.pool_slug === "foundryusa" ?
                                                        <div className="m-avatar is-size-xxl is-rounded" title="Foundry USA Pool">
                                                            <img
                                                                className="m-avatar-img"
                                                                src={row.pool_slug === "foundryusa" ? '/android-chrome-192x192.png' : ""}
                                                                alt="pool icon"
                                                            />
                                                        </div>
                                                        : <p className="m-text is-size-s">Other</p>
                                                }
                                            </div>
                                            <div className={cx(classes.cardRows, classes.cardRowContent)}>
                                                <p className="m-text">Blockheight</p>
                                                {loading ?
                                                    <Skeleton variant="rounded" width={"100%"} height={43} sx={{ bgcolor: 'var(--neutral-7)' }} />
                                                    :
                                                    <p className="m-text">{row.height}</p>
                                                }
                                            </div>
                                            <div className={cx(classes.cardRows, classes.cardRowContent)}>
                                                <p className="m-text">Status</p>
                                                {loading ?
                                                    <Skeleton variant="rounded" width={"100%"} height={43} sx={{ bgcolor: 'var(--neutral-7)' }} />
                                                    :
                                                    <p
                                                        className="m-text"
                                                        style={row.included ? {} : { color: "var(--color-text-link-on-danger)" }}
                                                    >
                                                        {row.included ? "Included" : "Excluded"}
                                                    </p>
                                                }
                                            </div>
                                            <div className={cx(classes.cardRows, classes.cardRowContent)}>
                                                <p className="m-text">Transaction Fees Per Block</p>
                                                {loading ?
                                                    <Skeleton variant="rounded" width={"100%"} height={43} sx={{ bgcolor: 'var(--neutral-7)' }} />
                                                    :
                                                    <p className="m-text">{row.total_fee_amt / 100000000}</p>
                                                }
                                            </div>
                                            <div className={cx(classes.cardRows, classes.cardRowContent)}>
                                                <p className="m-text">Block Subsidy</p>
                                                {loading ?
                                                    <Skeleton variant="rounded" width={"100%"} height={43} sx={{ bgcolor: 'var(--neutral-7)' }} />
                                                    :
                                                    <p className="m-text">{blockSubsidyByBlockHeight(row.height)}</p>
                                                }
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                            <div
                                className={classes.clickToExpand}
                                style={{
                                    opacity: collapse ? 0 : 1,
                                    cursor: collapse ? 'default' : 'pointer'
                                }}
                                onClick={() => setCollapse(true)}
                            >
                                <p className='m-text'>Click to expand</p>
                                <ArrowDownCircle
                                    className={cx('m-text', classes.downArrow)}
                                    size={16}
                                    style={{ transform: collapse ? 'rotate(180deg)' : 'rotate(0deg)' }}
                                />
                            </div>
                            <div
                                className={cx(classes.gradient, collapse ? classes.gradientOff : classes.gradientOn)}
                                onClick={() => setCollapse(true)}
                            ></div>
                        </div>
                    }

                    {mediumDevice ?
                        <div className={classes.calcBrkdnTtlsContainerMobile}>
                            <div className={classes.mobileTotalRow}>
                                <p className={cx('m-text is-size-l', classes.calcTtlsLabel)}>Total Network Blocks:</p>
                                {loading ?
                                    <Skeleton
                                        variant="rounded"
                                        className={classes.skeletonMobile}
                                        width={"20ch"}
                                        height={43}
                                        sx={{ bgcolor: 'var(--neutral-7)' }}
                                    />
                                    :
                                    <p className={cx('m-text is-size-l', classes.calcTtlsValue)}>{(blocksOfDay.length)}</p>
                                }
                            </div>
                            <div className={classes.mobileTotalRow}>
                                <p className={cx('m-text is-size-l', classes.calcTtlsLabel)}>Total Blocks Used For The PPLNS Rate Calculation:</p>
                                {loading ?
                                    <Skeleton
                                        variant="rounded"
                                        className={classes.skeletonMobile}
                                        width={"20ch"}
                                        height={43}
                                        sx={{ bgcolor: 'var(--neutral-7)' }}
                                    />
                                    :
                                    <p className={cx('m-text is-size-l', classes.calcTtlsValue)}>
                                        {dayjs(date).isBefore(dayjs('2023/11/30')) ? blocksOfDay.length : blocksOfDay.length - 6}
                                    </p>
                                }
                            </div>
                            <div
                                className={classes.mobileTotalRow}
                                style={{ display: dayjs(date).isBefore(dayjs('2023/11/30')) ? "none" : "flex" }}
                            >
                                <p className={cx('m-text is-size-l', classes.calcTtlsLabel)}>Total Fees Used For The PPLNS Rate Calculation:</p>
                                {loading ?
                                    <Skeleton
                                        variant="rounded"
                                        className={classes.skeletonMobile}
                                        width={"20ch"}
                                        height={43}
                                        sx={{ bgcolor: 'var(--neutral-7)' }}
                                    />
                                    :
                                    <p className={cx('m-text is-size-l', classes.calcTtlsValue)}>
                                        {totalFeesMinusExcluded / 100000000}
                                    </p>
                                }
                            </div>
                            <div className={classes.mobileTotalRow}>
                                <p className={cx('m-text is-size-l', classes.calcTtlsLabel)}>Total Subsidy For The PPLNS Rate Calculation:</p>
                                {loading ?
                                    <Skeleton
                                        variant="rounded"
                                        className={classes.skeletonMobile}
                                        width={"20ch"}
                                        height={43}
                                        sx={{ bgcolor: 'var(--neutral-7)' }}
                                    />
                                    :
                                    <p className={cx('m-text is-size-l', classes.calcTtlsValue)}>
                                        {dayjs(date).isBefore(dayjs('2023/11/30'))
                                            ? (blocksOfDay.length * blockSubsidy).toFixed(4).replace(/\.?0+$/, '')
                                            : ((blocksOfDay.length - 6) * blockSubsidy).toFixed(4).replace(/\.?0+$/, '')
                                        } BTC
                                    </p>
                                }
                            </div>
                            <div className={classes.mobileTotalRow}>
                                <div className={classes.flexRow}>
                                    <Link
                                        className={classes.mobileInfo}
                                        target='_blank'
                                        href='https://pool-faq.foundrydigital.com/how-is-PPLNS-rate-calculated' // TODO need a PPLNS doc & link
                                    >
                                        <p className={cx('m-text is-size-l', classes.calcTtlsLabel, classes.mobileInfoLabel)}>
                                            PPLNS Rate:
                                        </p>
                                    </Link>
                                </div>
                                {loading ?
                                    <Skeleton
                                        variant="rounded"
                                        className={classes.skeletonMobile}
                                        width={"20ch"}
                                        height={43}
                                        sx={{ bgcolor: 'var(--neutral-7)' }}
                                    />
                                    :
                                    <p className={cx('m-text is-size-l', classes.calcTtlsValue)}>
                                        {handlePPLNSCalc().toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}%
                                    </p>
                                }
                            </div>
                        </div>
                        :
                        <div className={classes.resultsBox}>
                            <div className={cx(classes.resultsEquation, classes.totals)}>
                                <p className='m-text is-size-l'>Total Network Blocks:</p>
                                {loading ?
                                    <Skeleton
                                        variant="rounded"
                                        className={classes.skeletonDesktop}
                                        width={"200px"}
                                        height={43}
                                        sx={{ bgcolor: 'var(--neutral-7)' }}
                                    />
                                    :
                                    <p className='m-text is-size-l'>{blocksOfDay.length}</p>
                                }
                            </div>
                            <div className={cx(classes.resultsEquation, classes.totals)}>
                                <p className='m-text is-size-l'>Total Blocks Used For The PPLNS Rate Calculation:</p>
                                {loading ?
                                    <Skeleton
                                        variant="rounded"
                                        className={classes.skeletonDesktop}
                                        width={"200px"}
                                        height={43}
                                        sx={{ bgcolor: 'var(--neutral-7)' }}
                                    />
                                    :
                                    <p className='m-text is-size-l'>
                                        {dayjs(date).isBefore(dayjs('2023/11/30')) ? blocksOfDay.length : blocksOfDay.length - 6}
                                    </p>
                                }
                            </div>
                            <div
                                className={cx(classes.resultsEquation, classes.totals)}
                                style={{ display: dayjs(date).isBefore(dayjs('2023/11/30')) && 'none' }}
                            >
                                <p className='m-text is-size-l'>Total Fees Used For The PPLNS Rate Calculation:</p>
                                {loading ?
                                    <Skeleton
                                        variant="rounded"
                                        className={classes.skeletonDesktop}
                                        width={"200px"}
                                        height={43}
                                        sx={{ bgcolor: 'var(--neutral-7)' }}
                                    />
                                    :
                                    <p className='m-text is-size-l'>{totalFeesMinusExcluded / 100000000}</p>
                                }
                            </div>
                            <div className={cx(classes.resultsEquation, classes.totals)}>
                                <p className='m-text is-size-l'>Total Subsidy For The PPLNS Rate Calculation:</p>
                                {loading ?
                                    <Skeleton
                                        variant="rounded"
                                        className={classes.skeletonDesktop}
                                        width={"200px"}
                                        height={43}
                                        sx={{ bgcolor: 'var(--neutral-7)' }}
                                    />
                                    :
                                    <p className='m-text is-size-l'>
                                        {dayjs(date).isBefore(dayjs('2023/11/30'))
                                            ? (blocksOfDay.length * blockSubsidy).toFixed(4).replace(/\.?0+$/, '')
                                            : ((blocksOfDay.length - 6) * blockSubsidy).toFixed(4).replace(/\.?0+$/, '')
                                        } BTC
                                    </p>
                                }
                            </div>
                            <div className={cx(classes.resultsEquation, classes.totals, classes.fppsRateRow)}>
                                <p className={cx('m-text is-size-l', classes.fppsRateTextWithMargin)}>PPLNS Rate:</p>
                                <div className={classes.infoIconContainer}>
                                    <Link
                                        target='_blank'
                                        href='https://pool-faq.foundrydigital.com/how-is-pplns-rate-calculated' // TODO need a PPLNS doc & link
                                    >
                                        <Info size={16} className='m-text' />
                                    </Link>
                                </div>
                                {loading ?
                                    <Skeleton
                                        variant="rounded"
                                        className={classes.skeletonDesktop}
                                        width={"200px"}
                                        height={43}
                                        sx={{ bgcolor: 'var(--neutral-7)' }}
                                    />
                                    :
                                    <p className='m-text is-size-l'>
                                        {handlePPLNSCalc().toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}%
                                    </p>
                                }
                            </div>
                        </div>
                    }
                </div>
            </div>
        </section>
    );
};

export default PPLNSCalculationBreakdown;